import { useCallback, useEffect, useState } from 'react';
import { usePermission } from '../../../../auth/AuthContext';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdDateTime, TdInput, TdSelect } from '../../../../components/inline-add-table/cells';
import { Select } from '../../../../components/inputs/Select';
import { ApiError } from '../../../../lib/apiError';
import { toDateTimeLocal } from '../../../../lib/formatDate';
import { focusValidationError } from '../../../../lib/validationFocus';
import { evidenceRequestsApi } from '../../../self-service/evidence-requests/evidenceRequestsApi';
import { evidenceItemsApi, evidenceTypesApi } from '../evidenceApi';
import {
  EVIDENCE_EXTENSIONS,
  MAX_ATTACHMENTS,
  MAX_INTAKE_ROWS,
  type EvidenceBatchResult,
  type EvidenceRequestAttachmentItem,
  type EvidenceRequestCaseOption,
  type EvidenceRequestDetail,
  type EvidenceRequestResult,
  type EvidenceTermOption,
} from '../evidenceTypes';
import { useStaffOptions } from '../../../human-resources/personnel/useStaff';
import { sampleIntake, samplePdf } from '../utils/sampleEvidence';
import { EvidenceFilesCell } from './EvidenceFilesField';

/*
  A type the reader typed into a row's picker is STAGED, not written — the `<InventoryItemModal>`
  rule. Its value is a SENTINEL carrying the name, keyed on the name, so two rows that typed the
  same word stage ONE row; `save()` creates each staged word under its own key immediately before
  the intake, then swaps the sentinels for the ids it got back.
*/
const PENDING_TYPE = 'pending:type:';

interface Row {
  key: string;
  typeId: string;
  description: string;
  /** `datetime-local`, in the reader's own zone. Converted to an instant on save. */
  acquiredAt: string;
  /** "Acquired by" — who actually acquired THIS item. Not the agent, who submits the whole event. */
  acquiredById: string;
  attachments: File[];
  /** The REQUEST line this row came from, when receiving a request — its documents ride along. */
  requestLineId: string | null;
  /** That line's documents, already filed. Shown, counted, never removable here. */
  carried: EvidenceRequestAttachmentItem[];
}

let rowSeed = 0;

function blankRow(from?: Row): Row {
  rowSeed += 1;

  /*
    A NEW ROW CARRIES THE PREVIOUS ROW'S TIME AND ACQUIRER — one receiving event is usually one
    seizure at one moment. What the item IS, its description and its attachments are never
    carried: those are what make it a different item.
  */
  return {
    key: `row-${rowSeed}`,
    typeId: '',
    description: '',
    acquiredAt: from?.acquiredAt ?? '',
    acquiredById: from?.acquiredById ?? '',
    attachments: [],
    requestLineId: null,
    carried: [],
  };
}

/** A request's lines as rows — normalised at the seam (`?? ''`), so nothing downstream meets `undefined`. */
function rowsFromRequest(request: EvidenceRequestDetail): Row[] {
  return request.lines.map((line) => ({
    ...blankRow(),
    typeId: line.evidenceTypeId ?? '',
    description: line.description ?? '',
    acquiredAt: toDateTimeLocal(line.acquiredAt ?? null),
    acquiredById: line.acquiredByPersonnelId ?? '',
    requestLineId: line.id,
    carried: line.attachments,
  }));
}

/** The staged name behind a sentinel, or null for a real id. */
function stagedName(value: string, prefix: string): string | null {
  return value.startsWith(prefix) ? value.slice(prefix.length) : null;
}

type IntakeProps =
  | {
      /** RECEIVE — the case's own form. */
      mode?: 'receive';
      /**
       * The case the form was opened from — its profile's Evidence tab. NO case field (product call,
       * 08 Oct 2026): the evidence belongs to the case on screen.
       */
      caseId: string;
      /**
       * The pending RECEIVING request this receive fulfils (product call, 09 Oct 2026): its lines open
       * as the rows, its requester as the agent, and its documents ride onto each item.
       */
      fromRequest?: EvidenceRequestDetail | null;
      onClose: () => void;
      onSaved: (saved: EvidenceBatchResult) => void;
    }
  | {
      /**
       * RAISE A RECEIVING REQUEST — Self Service (product call, 09 Oct 2026). The same rows; the header is
       * the CASE (one the requester is a member of) in place of the custodian and agent: the requester IS
       * the agent, and the member who receives it names the custodian.
       */
      mode: 'request';
      onClose: () => void;
      onRequested: (saved: EvidenceRequestResult) => void;
    };

/**
 * RECEIVES evidence — one receiving event, several items, every item's first custody line
 * (received) and its attachments, in one request, all or nothing.
 *
 * <b>Step 1 of the cycle — RECEIVING</b> (product calls, 08 Oct 2026). The HEADER is what the event
 * shares — the CASE is the one on screen, never a field (08 Oct 2026): the CUSTODIAN receiving it — the signed-in employee by default —
 * and the AGENT submitting it. Each ROW is one item: its type, description, when it was acquired,
 * who ACQUIRED it, and its attachments. Received evidence waits FOR EVALUATION, in no location.
 *
 * <b>No reason, no evidence file on a row</b> (08 Oct 2026): the received line's reason is the act,
 * "Received"; a file is an attachment like any other. <b>Attachments are optional</b>, up to the cap.
 * <b>No approval and no password.</b>
 *
 * <b>The same form RAISES a receiving request</b> from Self Service (`mode="request"`) — one grid, so a
 * request cannot ask for an item the Receive form could not then record.
 *
 * Autogenerate fills the header and two rows, attaching a synthetic PDF that says it is one — PDF
 * has a textual form the signature check honestly accepts.
 */
export function EvidenceIntakeModal(props: IntakeProps) {
  const asRequest = props.mode === 'request';
  const fromRequest = props.mode === 'request' ? null : (props.fromRequest ?? null);
  const ID = asRequest ? 'evidence-request-receiving' : 'evidence-intake';

  const canReadPersonnel = usePermission('personnel.read');
  const canCreateType = usePermission('evidence-types.create');
  const { options: staff, loading: loadingStaff } = useStaffOptions(true);

  const [caseId, setCaseId] = useState(props.mode === 'request' ? '' : props.caseId);
  const [custodianId, setCustodianId] = useState('');
  // A request's REQUESTER is the agent who submits it — product call, 09 Oct 2026.
  const [agentId, setAgentId] = useState(fromRequest?.requesterPersonnelId ?? '');
  const [rows, setRows] = useState<Row[]>(() => (fromRequest ? rowsFromRequest(fromRequest) : []));

  const [types, setTypes] = useState<EvidenceTermOption[]>([]);
  const [cases, setCases] = useState<EvidenceRequestCaseOption[]>([]);
  const [loadingCatalogs, setLoadingCatalogs] = useState(true);

  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const quiet = <T,>(promise: Promise<T>, fallback: T) => promise.catch(() => fallback);

    // A failed catalog load names no field, so it never reaches the save path's error state.
    void Promise.all([
      quiet(evidenceTypesApi.options(controller.signal), []),
      asRequest ? Promise.resolve(null) : quiet(evidenceItemsApi.eligibility(controller.signal), null),
      asRequest ? quiet(evidenceRequestsApi.cases(controller.signal), []) : Promise.resolve([]),
    ]).then(([nextTypes, eligibility, nextCases]) => {
      if (controller.signal.aborted) return;

      setTypes(nextTypes);
      setCases(nextCases);

      /*
        THE CUSTODIAN DEFAULTS TO THE SIGNED-IN EMPLOYEE — product call, 08 Oct 2026. Only while
        the reader has not chosen one; the server returns their OWN record and nobody else's.
      */
      const mine = eligibility?.personnelId;
      if (mine) setCustodianId((current) => current || mine);

      setLoadingCatalogs(false);
    });

    return () => controller.abort();
  }, [asRequest]);

  const addRow = useCallback(() => {
    setRows((current) => (current.length >= MAX_INTAKE_ROWS ? current : [...current, blankRow(current.at(-1))]));
  }, []);

  function patch(key: string, change: Partial<Row>) {
    setRows((current) => current.map((r) => (r.key === key ? { ...r, ...change } : r)));
  }

  /*
    A BLANK ROW IS DROPPED, never refused — the `<InlineAddTable>` contract, kept on the CLIENT
    because the server refuses a blank item. Blank is "nothing the reader typed": no type, no
    description, no attachments. The time and acquirer a new row CARRIES do not count — and a row
    from a request is never blank. The server names rows by their SUBMITTED index, so every cell
    id below is built from that index, and a blank row's from its own key.
  */
  const isBlank = (row: Row) =>
    row.requestLineId === null && row.typeId === '' && row.description.trim() === '' && row.attachments.length === 0;
  const filled = rows.filter((r) => !isBlank(r));

  // A time in the future is the SERVER's to refuse — it names the cell, and its clock is the one that counts.
  function rowMissing(row: Row): string | null {
    if (row.typeId === '') return 'choose what it is';
    if (row.description.trim() === '') return 'describe it';
    if (row.acquiredAt === '') return 'enter when it was acquired';
    if (row.acquiredById === '') return 'choose who acquired it';
    return null;
  }

  function missing(): string | null {
    if (asRequest) {
      if (caseId === '') return 'Choose the case.';
    } else {
      if (custodianId === '') return 'Choose the custodian receiving it.';
      if (agentId === '') return 'Choose the agent submitting it.';
    }

    if (filled.length === 0) return 'Add at least one item.';

    for (const row of filled) {
      const gap = rowMissing(row);
      if (gap) return `Item ${rows.indexOf(row) + 1}: ${gap}.`;
    }

    return null;
  }

  const gap = missing();
  const lastIncomplete = rows.length > 0 && rowMissing(rows[rows.length - 1]) !== null;
  const canSave = gap === null && confirmed && !saving;

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      /*
        THE STAGED WORDS FIRST, each under its OWN key — folding them into the intake would be
        `evidence-items.create` silently writing rows `evidence-types.create` governs. A rejected
        intake leaves them behind; that window is one request wide instead of the whole time the
        form was open.
      */
      const resolved = new Map<string, string>();

      for (const sentinel of new Set(filled.map((r) => r.typeId))) {
        const typeName = stagedName(sentinel, PENDING_TYPE);
        if (typeName === null) continue;

        const created = await evidenceTypesApi.create({ name: typeName, description: null });
        resolved.set(sentinel, created.id);
        setTypes((current) =>
          current.some((t) => t.id === created.id) ? current : [...current, { id: created.id, name: created.name }],
        );
      }

      if (resolved.size > 0) {
        setRows((current) => current.map((r) => ({ ...r, typeId: resolved.get(r.typeId) ?? r.typeId })));
      }

      if (props.mode === 'request') {
        const raised = await evidenceRequestsApi.raiseReceiving(
          {
            evidenceCaseId: caseId,
            rows: filled.map((r) => ({
              evidenceTypeId: resolved.get(r.typeId) ?? r.typeId,
              description: r.description.trim(),
              acquiredAt: new Date(r.acquiredAt).toISOString(),
              acquiredByPersonnelId: r.acquiredById,
            })),
          },
          filled.map((r) => r.attachments),
        );

        props.onRequested(raised);
        return;
      }

      const saved = await evidenceItemsApi.store(
        {
          evidenceCaseId: caseId,
          custodianPersonnelId: custodianId,
          submittingAgentPersonnelId: agentId,
          evidenceRequestId: fromRequest?.id ?? null,
          rows: filled.map((r) => ({
            evidenceTypeId: resolved.get(r.typeId) ?? r.typeId,
            description: r.description.trim(),
            acquiredAt: new Date(r.acquiredAt).toISOString(),
            agentPersonnelId: r.acquiredById,
            requestLineId: r.requestLineId,
          })),
        },
        filled.map((r) => ({ file: null, attachments: r.attachments })),
      );

      props.onSaved(saved);
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not receive the evidence.', 0);

      setError(failure);
      focusValidationError(failure, { idPrefix: ID });
    } finally {
      setSaving(false);
    }
  }

  // The staged words are offered to EVERY row, so a second row can pick what the first typed.
  const stagedTypes = [...new Set(rows.map((r) => r.typeId).filter((v) => v.startsWith(PENDING_TYPE)))];
  const typeOptions = [
    ...stagedTypes.map((v) => ({ value: v, label: stagedName(v, PENDING_TYPE) ?? v })),
    ...types.map((t) => ({ value: t.id, label: t.name })),
  ];

  // The REQUEST's field is `acquiredByPersonnelId`; the intake's row keeps `agentPersonnelId`.
  const acquirerField = asRequest ? 'acquired-by-personnel-id' : 'agent-personnel-id';

  return (
    <Modal
      open
      onClose={props.onClose}
      title={asRequest ? 'Receiving request' : 'Receive evidence'}
      size="3xl"
      id={`${ID}-modal`}
      loading={loadingCatalogs}
      autogenerate={{
        onGenerate: () => {
          const sample = sampleIntake({
            caseIds: asRequest ? cases.map((c) => c.id) : [caseId],
            staffIds: staff.map((s) => s.value),
            typeIds: types.map((t) => t.id),
          });

          if (asRequest) {
            if (caseId === '') setCaseId(sample.evidenceCaseId);
          } else {
            if (custodianId === '') setCustodianId(sample.custodianPersonnelId);
            // A request's agent is its requester — the reader has not decided it, the request has.
            if (!fromRequest) setAgentId(sample.submittingAgentPersonnelId);
          }

          // An empty type catalog is answered by STAGING a synthetic word, where the reader may add one.
          const fallbackType = canCreateType ? `${PENDING_TYPE}Sample type` : '';

          // Rows from a request are what the requester described — Autogenerate leaves them alone.
          if (!fromRequest) {
            setRows(
              sample.rows.map((r, index) => ({
                ...blankRow(),
                typeId: r.evidenceTypeId || fallbackType,
                description: r.description,
                acquiredAt: r.acquiredAt,
                acquiredById: r.agentPersonnelId,
                attachments: [samplePdf(`sample-attachment-${index + 1}.pdf`)],
              })),
            );
          }
        },
        disabled: saving || loadingCatalogs,
      }}
      confirm={{ id: `${ID}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID}-cancel`} variant="secondary" onClick={props.onClose} disabled={saving}>
            Cancel
          </Button>

          <Tooltip disabledReason={gap}>
            <Button
              id={`${ID}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {fromRequest ? (
          <div role="note" className="rounded-lg border border-slate-200 bg-slate-50 p-3">
            <p className="text-xs text-slate-600">
              From request <span className="font-mono">{fromRequest.requestNumber}</span>. Its items are below, as the
              requester described them, and the requester is the agent. Correct anything before submitting — the
              documents they filed are carried onto each item.
            </p>
          </div>
        ) : null}

        {asRequest ? (
          <Select
            id={`${ID}-evidence-case-id`}
            label="Case"
            required
            description="A case you are a member of. Its team receives the items; you are recorded as the agent submitting them."
            placeholder={!loadingCatalogs && cases.length === 0 ? 'You are not a member of any case' : 'Choose a case'}
            value={caseId}
            onChange={setCaseId}
            options={cases.map((c) => ({ value: c.id, label: c.title, sublabel: c.caseNumber, keywords: c.caseNumber }))}
            remote
            loading={loadingCatalogs}
            disabled={saving || (!loadingCatalogs && cases.length === 0)}
            error={error?.fieldError('evidenceCaseId')}
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {canReadPersonnel ? (
              <>
                <Select
                  id={`${ID}-custodian-personnel-id`}
                  label="Custodian"
                  required
                  description="The custodian receiving every item below and accountable for them from now on. Defaults to you."
                  placeholder="Choose an employee"
                  value={custodianId}
                  onChange={setCustodianId}
                  options={staff}
                  remote
                  loading={loadingStaff}
                  disabled={saving}
                  error={error?.fieldError('custodianPersonnelId')}
                />

                <Select
                  id={`${ID}-submitting-agent-personnel-id`}
                  label="Agent"
                  required
                  description="The agent submitting this evidence. Not the person who acquired each item — that is each row's Acquired by."
                  placeholder="Choose an employee"
                  value={agentId}
                  onChange={setAgentId}
                  options={staff}
                  remote
                  loading={loadingStaff}
                  disabled={saving}
                  error={error?.fieldError('submittingAgentPersonnelId')}
                />
              </>
            ) : null}
          </div>
        )}

        <InlineAddTable
          headers={['Type *', 'Description *', 'Acquired *', 'Acquired by *', 'Attachments']}
          colWidths={[160, 280, 200, 200, 170]}
          flexColumn={1}
          resizeKey="evidence-intake-rows-v4"
          rowCount={rows.length}
          onAddRow={addRow}
          addDisabled={saving || lastIncomplete || rows.length >= MAX_INTAKE_ROWS}
          addDisabledReason={
            rows.length >= MAX_INTAKE_ROWS ? `At most ${MAX_INTAKE_ROWS} items can be received at once.` : undefined
          }
          addButtonId={`${ID}-add-row`}
        >
          {rows.map((row, index) => {
            const submitted = filled.indexOf(row);
            const at = submitted === -1 ? `${ID}-${row.key}` : `${ID}-rows.${submitted}`;
            const itemLabel = `item ${index + 1}`;

            return (
              <InlineRow
                key={row.key}
                idx={index + 1}
                onRemove={() => setRows((current) => current.filter((r) => r.key !== row.key))}
              >
                <TdSelect
                  id={`${at}.evidence-type-id`}
                  ariaLabel={`Type of ${itemLabel}`}
                  value={row.typeId || null}
                  onChange={(next) => patch(row.key, { typeId: next })}
                  options={typeOptions}
                  remote
                  allowCustom={canCreateType}
                  onCreateOption={
                    canCreateType ? (raw) => patch(row.key, { typeId: `${PENDING_TYPE}${raw.trim()}` }) : undefined
                  }
                  disabled={saving}
                />

                <TdInput
                  id={`${at}.description`}
                  ariaLabel={`Description of ${itemLabel}`}
                  placeholder="As written on the inventory of seized items"
                  value={row.description}
                  onChange={(next) => patch(row.key, { description: next })}
                  disabled={saving}
                />

                <TdDateTime
                  id={`${at}.acquired-at`}
                  ariaLabel={`When ${itemLabel} was acquired`}
                  value={row.acquiredAt}
                  onChange={(next) => patch(row.key, { acquiredAt: next })}
                  disabled={saving}
                />

                {canReadPersonnel ? (
                  <TdSelect
                    id={`${at}.${acquirerField}`}
                    ariaLabel={`Who acquired ${itemLabel}`}
                    value={row.acquiredById || null}
                    onChange={(next) => patch(row.key, { acquiredById: next })}
                    options={staff}
                    remote
                    disabled={saving}
                  />
                ) : (
                  <td className="border-l border-slate-100 px-2 text-sm text-slate-400">—</td>
                )}

                <EvidenceFilesCell
                  id={`${at}.attachments`}
                  ariaLabel={`Attachments for ${itemLabel}`}
                  multiple
                  max={MAX_ATTACHMENTS}
                  accept={EVIDENCE_EXTENSIONS}
                  files={row.attachments}
                  carried={row.carried}
                  onChange={(next) => patch(row.key, { attachments: next })}
                  disabled={saving}
                  invalid={submitted !== -1 && Boolean(error?.fieldError(`rows.${submitted}.attachments`))}
                />
              </InlineRow>
            );
          })}
        </InlineAddTable>

        <p className="text-xs text-slate-500">
          {asRequest
            ? `Nothing is received yet: the case's team sees this request under the case's Requests, and receives the items — or declines, with a reason. Attachments are optional, up to ${MAX_ATTACHMENTS} per item.`
            : `Each item gets its own inventory number and line 1 of its own chain of custody, and waits for evaluation — accepted into storage, or deferred outside the facility. Attachments are optional — the receipt, a photograph, the signed form or the file itself — up to ${MAX_ATTACHMENTS} per item.`}
        </p>
      </div>
    </Modal>
  );
}
